import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, MapPin, Users, X } from "lucide-react";
import { people } from "../../data/demoContent";
import { FrostLayer } from "../FrostLayer";

const STEPS = ["Introduction proposed", "Waiting for both to accept", "Chat opens"];

export const Stepper = ({ at }) => (
  <ol className="mt-5 grid grid-cols-3 gap-2" data-testid="connection-stepper">
    {STEPS.map((s, i) => (
      <li key={s} className={`rounded-2xl px-3 py-2 text-[11.5px] font-semibold leading-tight ${i < at ? "bg-[#08090D] text-white" : i === at ? "bg-white/80 text-[#08090D] ring-1 ring-[#A79DE2]" : "bg-white/40 text-[#777B8D]"}`}>
        <span className="mb-1 flex items-center gap-1">{i < at ? <Check size={12} /> : `0${i + 1}`}</span>
        {s}
      </li>
    ))}
  </ol>
);

const Face = ({ src, alt }) => <img src={src} alt={alt} className="h-14 w-14 rounded-full border-2 border-white object-cover shadow-md" />;

export const Pair = ({ person }) => (
  <div className="mt-6 flex items-center justify-center gap-3">
    <Face src={people.karna.avatar} alt="Karna" />
    <span className="h-px w-14 bg-gradient-to-r from-[#7866E8] to-[#80D9EB]" aria-hidden />
    <Face src={person.avatar} alt={person.name} />
  </div>
);

export const ProfileStage = ({ person, onPropose }) => (
  <div data-testid="connection-profile">
    <div className="relative overflow-hidden rounded-[26px]">
      <img src={person.image} alt={person.alt} className="aspect-[4/3] w-full object-cover" style={{ objectPosition: "50% 22%" }} />
      <FrostLayer src={person.image} height="40%" />
      <p className="absolute inset-x-5 bottom-4 z-[2] text-[15px] font-semibold text-white">{person.stat}</p>
    </div>
    <p className="mt-5 text-[15px] font-medium text-[#4c5064]">{person.bio}</p>
    <p className="mt-3 text-[16px] leading-relaxed text-[#3c4052]">{person.about}</p>
    <p className="mt-3 flex items-center gap-2 text-[13px] text-[#5b5f73]"><MapPin size={14} aria-hidden /> {person.location}</p>
    <p className="mt-1 flex items-center gap-2 text-[13px] text-[#5b5f73]"><Users size={14} aria-hidden /> {person.mutuals}</p>
    <ul className="mt-4 flex flex-wrap gap-2">{person.interests.map((i) => <li key={i} className="rounded-full bg-white/75 px-3 py-1 text-[12px] font-semibold text-[#14151B]">{i}</li>)}</ul>
    <button type="button" onClick={onPropose} data-testid="connection-propose-button" className="btn-primary mt-6 h-13 w-full py-4 text-[15px]">Propose Introduction</button>
  </div>
);

const Slot = ({ face, name, state }) => (
  <div className="rounded-[22px] bg-white/60 p-3 text-center" data-testid={`accept-slot-${name.toLowerCase()}`}>
    <img src={face} alt="" className="mx-auto h-10 w-10 rounded-full object-cover" />
    <p className="mt-2 text-[13px] font-semibold text-[#08090D]">{name}</p>
    <p className="mt-1 flex items-center justify-center gap-1 text-[12px] text-[#5b5f73]">{state}</p>
  </div>
);

export const ProposedStage = ({ person, karna, other, onAccept, onDecline }) => {
  const otherState = {
    idle: "Not yet responded",
    reviewing: <><Loader2 size={12} className="animate-spin" aria-hidden /> Reviewing… (sample)</>,
    accepted: <><Check size={12} className="text-[#7866E8]" aria-hidden /> Accepted (simulated)</>,
  }[other];
  return (
    <div data-testid="connection-proposed">
      <Stepper at={1} />
      <Pair person={person} />
      <p className="mt-4 rounded-2xl bg-white/55 px-4 py-3 text-center text-[14px] leading-snug text-[#3c4052]">
        <span className="font-semibold text-[#08090D]">Why: </span>{person.why}
      </p>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <Slot face={people.karna.avatar} name="Karna" state={karna ? <><Check size={12} className="text-[#7866E8]" aria-hidden /> Accepted</> : "Your choice"} />
        <Slot face={person.avatar} name={person.name} state={otherState} />
      </div>
      {!karna && (
        <div className="mt-5 grid grid-cols-[1fr_auto] gap-2">
          <button type="button" onClick={onAccept} data-testid="connection-accept-karna" className="btn-primary h-12 text-[15px]">Accept as Karna</button>
          <button type="button" onClick={onDecline} data-testid="connection-decline" className="h-12 rounded-full bg-white/70 px-5 text-[14px] font-semibold text-[#08090D] hover:bg-white">Decline</button>
        </div>
      )}
      <p className="mt-3 text-center text-[12px] text-[#777B8D]">No chat exists until both people accept.</p>
    </div>
  );
};

export const ConnectedStage = ({ person, onDone }) => {
  const [replied, setReplied] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReplied(true), 1300);
    return () => clearTimeout(t);
  }, []);
  return (
    <div data-testid="connection-connected">
      <Stepper at={3} />
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: "spring", stiffness: 220, damping: 26 }} className="sheet mt-5 rounded-[36px] pb-4" data-testid="connection-new-chat">
        <div className="flex justify-center pb-2 pt-3"><span className="sheet-handle" aria-hidden /></div>
        <div className="flex items-center gap-2.5 border-b border-white/[0.07] px-4 pb-3">
          <img src={person.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
          <div className="flex-1">
            <p className="text-[14px] font-semibold text-white">{person.name}</p>
            <p className="text-[11px] text-[#A5E6E3]">New chat · both accepted</p>
          </div>
        </div>
        <div className="space-y-2 px-4 pt-3">
          <div className="flex justify-end"><p className="bubble-me max-w-[80%] rounded-[20px] rounded-br-[8px] px-4 py-2.5 text-[14px]">{person.firstMessage}</p></div>
          <div className="flex justify-start">
            <p className="bubble-them max-w-[80%] rounded-[20px] rounded-bl-[8px] px-4 py-2.5 text-[14px]">{replied ? person.reply : <span className="typing" aria-label="typing"><i /><i /><i /></span>}</p>
          </div>
        </div>
      </motion.div>
      <p className="mt-3 text-center text-[12px] text-[#777B8D]">Chat created after mutual acceptance — sample demo</p>
      <button type="button" onClick={onDone} data-testid="connection-done" className="btn-primary mt-4 h-12 w-full text-[15px]">Done</button>
    </div>
  );
};

export const DeclinedStage = ({ onRestart, onClose }) => (
  <div className="py-4 text-center" data-testid="connection-declined" role="status">
    <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#08090D] text-white"><X size={22} aria-hidden /></span>
    <p className="mt-4 text-[20px] font-bold text-[#08090D]">Introduction declined</p>
    <p className="mt-2 text-[15px] text-[#3c4052]">No chat is created without mutual acceptance.</p>
    <div className="mt-6 flex justify-center gap-2">
      <button type="button" onClick={onRestart} data-testid="connection-restart" className="h-11 rounded-full bg-white/75 px-5 text-[14px] font-semibold text-[#08090D]">Start over</button>
      <button type="button" onClick={onClose} className="btn-primary h-11 px-6 text-[14px]">Close</button>
    </div>
  </div>
);
